import Vue from "vue";
import VueI18n from "vue-i18n";

Vue.use(VueI18n);

const STORAGE_KEY = "kalendario.locale";
const SUPPORTED_LOCALES = ["es", "en", "pt"];

const messages = {
  es: {
    common: {
      create: "Crear",
      cancel: "Cancelar",
      close: "Cerrar",
      delete: "Eliminar",
      leave: "Salir",
      invite: "Invitar",
      copy: "Copiar",
      copied: "Copiado",
      install: "Instalar",
      notNow: "Ahora no",
      accept: "Aceptar",
      decline: "Rechazar",
      yes: "Sí",
      no: "No",
      ok: "OK",
      clear: "Limpiar",
      save: "Guardar",
      event: "Evento",
      group: "Grupo",
      admin: "admin",
      user: "Usuario",
      status: "Estado"
    },
    languages: {
      es: "Español",
      en: "Inglés",
      pt: "Portugués"
    },
    nav: {
      calendar: "Calendario",
      groups: "Grupos",
      requests: "Solicitudes",
      profile: "Perfil"
    },
    login: {
      subtitle: "Entra o crea una cuenta para compartir calendarios con tus grupos.",
      welcomeTitle: "Bienvenido a Kalendar.io",
      signInTab: "Entrar",
      registerTab: "Crear cuenta",
      nameLabel: "Nombre",
      emailLabel: "Correo electrónico",
      passwordLabel: "Contraseña",
      forgotPassword: "¿Has olvidado tu contraseña?",
      resetPasswordTitle: "Introduce tu correo electrónico",
      resetPasswordHelp: "Te enviaremos un enlace para crear una nueva contraseña.",
      resetPasswordButton: "Solicitar cambio de contraseña",
      backToSignIn: "Volver a iniciar sesión",
      emailButton: "Entrar con correo",
      registerButton: "Crear cuenta",
      googleButton: "Entrar con Google",
      languageLabel: "Idioma",
      cookiesPolicy: "Política de cookies",
      legalNotice: "Aviso legal",
      privacyPolicy: "Política de privacidad",
      acceptLegalPrefix: "He leído y acepto el",
      acceptLegalAnd: "y he leído la",
      acceptLegalRequired: "Debes aceptar el aviso legal y confirmar que has leído la política de privacidad para crear una cuenta.",
      continueLegalNotice: "Al continuar aceptas el",
      continueLegalAnd: "y confirmas que has leído la",
      loginError: "Error al iniciar sesión",
      authProviderDisabled: "Este método de inicio de sesión no está habilitado en Firebase. Activa el proveedor correspondiente en Firebase Authentication.",
      emailVerificationSent: "Te hemos enviado un correo de verificación. Confirma tu email antes de iniciar sesión.",
      emailVerificationRequired: "Debes confirmar tu correo electrónico antes de entrar. Te hemos reenviado el email de verificación.",
      emailVerifiedNotice: "Correo verificado. Ya puedes iniciar sesión.",
      passwordResetSent: "Te hemos enviado un correo para restablecer tu contraseña.",
      passwordResetNotice: "Contraseña actualizada. Ya puedes iniciar sesión.",
      requiredField: "Campo obligatorio",
      invalidEmail: "Correo electrónico no válido",
      shortPassword: "La contraseña debe tener al menos 6 caracteres",
      firebaseAuthErrors: {
        accountExistsWithDifferentCredential: "Ya existe una cuenta con este correo usando otro método de inicio de sesión.",
        credentialAlreadyInUse: "Esta credencial ya está asociada a otra cuenta.",
        emailAlreadyInUse: "Ya existe una cuenta con este correo electrónico.",
        expiredActionCode: "El enlace ha caducado. Solicita uno nuevo.",
        invalidActionCode: "El enlace no es válido o ya se ha utilizado.",
        invalidCredential: "Correo o contraseña incorrectos.",
        invalidEmail: "El correo electrónico no es válido.",
        missingPassword: "Introduce tu contraseña.",
        networkRequestFailed: "No se pudo conectar. Revisa tu conexión e inténtalo de nuevo.",
        operationNotAllowed: "Este método de inicio de sesión no está habilitado en Firebase. Activa el proveedor correspondiente en Firebase Authentication.",
        popupBlocked: "El navegador ha bloqueado la ventana de inicio de sesión.",
        popupClosedByUser: "Has cerrado la ventana de inicio de sesión antes de completarlo.",
        requiresRecentLogin: "Por seguridad, vuelve a iniciar sesión e inténtalo de nuevo.",
        tooManyRequests: "Demasiados intentos. Espera unos minutos e inténtalo de nuevo.",
        userDisabled: "Esta cuenta ha sido deshabilitada.",
        userNotFound: "No existe ninguna cuenta con este correo electrónico.",
        weakPassword: "La contraseña es demasiado débil. Usa al menos 6 caracteres.",
        wrongPassword: "La contraseña no es correcta."
      }
    },
    landing: {
      kicker: "Calendarios compartidos sin ruido",
      title: "Organiza planes con tus grupos en un solo lugar.",
      subtitle: "Kalendar.io te ayuda a crear eventos, compartirlos con grupos y gestionar solicitudes de participación para que todos sepan qué ocurre y cuándo.",
      cta: "Empezar",
      playStore: "Disponible en Google Play",
      rightsReserved: "Todos los derechos reservados.",
      signIn: "Entrar",
      featuresLabel: "Funciones principales",
      featureCalendarTitle: "Un calendario para cada plan",
      featureCalendarText: "Crea eventos con fecha, horario y contexto para consultar tu agenda de forma clara.",
      featureGroupsTitle: "Grupos conectados",
      featureGroupsText: "Comparte calendarios con familia, amigos o equipos y decide quién visualiza o participa.",
      featureRequestsTitle: "Solicitudes y avisos",
      featureRequestsText: "Recibe solicitudes de participación y mantén a tu grupo al día con notificaciones.",
      previewMonth: "Mayo 2026",
      previewEventOne: "Turno de tarde",
      previewGroupOne: "Equipo",
      previewEventTwo: "Cena familiar",
      previewGroupTwo: "Familia",
      demoCreateTitle: "Crea un evento",
      demoCreateText: "Añade fecha, hora y detalles.",
      demoShareTitle: "Compártelo con grupos",
      demoShareText: "Elige quién participa o visualiza.",
      demoRequestTitle: "Nueva solicitud",
      demoRequestText: "Tu grupo recibe el aviso al momento."
    },
    calendar: {
      addEvent: "Evento",
      editEvent: "Editar",
      viewing: "Visualizo",
      participating: "Participo",
      mixedAccess: "Participa y visualiza",
      deleteEvent: "Eliminar",
      noEventsDay: "No hay eventos para este día.",
      selectDay: "Selecciona un día para ver sus eventos",
      filters: "Filtros",
      visibility: "Visualización",
      all: "Todos",
      none: "Ninguno",
      allDay: "Todo el día",
      groupFallback: "Grupo {id}",
      noEventId: "Este evento no tiene id (no se puede eliminar).",
      confirmDeleteEvent: "¿Eliminar este evento?",
      deleteEventError: "No se pudo eliminar el evento.",
      updateEventError: "No se pudo guardar el evento."
    },
    groups: {
      createGroup: "Crear grupo",
      name: "Nombre",
      noMembers: "Aún no hay miembros.",
      shareLink: "Comparte este enlace.",
      copyError: "No se pudo copiar. Copia manualmente el enlace.",
      inviteTitle: "Invitar",
      deleteGroupConfirm: "¿Eliminar este grupo?\n\nSe borrarán también sus eventos y miembros.",
      deleteGroupError: "No se pudo eliminar el grupo.",
      leaveGroupConfirm: "¿Seguro que quieres salir de este grupo?",
      leaveGroupError: "No se pudo salir del grupo.",
      webShareTitle: "Invitación a Kalendar.io",
      webShareText: "Únete a mi grupo en Kalendar.io"
    },
    requests: {
      title: "Solicitudes",
      loginWarning: "Inicia sesión para ver tus solicitudes.",
      pendingTab: "Pendientes ({count})",
      archivedTab: "Archivadas ({count})",
      noPending: "No tienes solicitudes pendientes.",
      noArchived: "No tienes solicitudes archivadas.",
      participation: "Participación",
      declined: "Rechazada",
      requestLoadError: "No se pudieron cargar las solicitudes.",
      acceptError: "No se pudo aceptar la solicitud.",
      declineError: "No se pudo rechazar la solicitud.",
      declinedToast: "Solicitud rechazada (puedes aceptarla más tarde).",
      noDate: "Sin fecha",
      requestedBy: "Solicitado por {name}"
    },
    profile: {
      title: "Perfil",
      settingsTitle: "Ajustes",
      settingsDescription: "Gestiona tus preferencias de cuenta, idioma y notificaciones.",
      logout: "Salir",
      deleteAccount: "Eliminar cuenta",
      languageSettings: "Idioma de la aplicación",
      languageDescription: "Elige cómo quieres ver toda la interfaz.",
      notifications: "Notificaciones",
      notificationSettings: "Ajustes de notificaciones",
      notificationDescription: "Recibe una notificación cuando alguien solicite tu participación en un evento.",
      notificationLabel: "Solicitudes de participación en eventos",
      accountSettings: "Cuenta",
      session: "Sesión",
      sessionDescription: "Cierra tu sesión en este dispositivo.",
      dangerZone: "Eliminar cuenta",
      dangerZoneDescription: "Borra tu cuenta y todos tus datos de forma permanente.",
      deleteAccountConfirmTitle: "¿Eliminar cuenta?",
      deleteAccountConfirmBody: "Esta acción es permanente y no se puede deshacer. Se eliminará tu cuenta y perderás todos los datos.",
      recentLoginNeeded: "Por seguridad, vuelve a iniciar sesión y repite la eliminación de cuenta.",
      deleteAccountError: "Error al eliminar la cuenta.",
      saveNotificationError: "No se pudo guardar el ajuste de notificaciones."
    },
    inviteAccept: {
      title: "Invitación",
      loginHint: "Inicia sesión con Google para unirte al grupo.",
      joinHint: "Pulsa para unirte al grupo.",
      joinButton: "Unirme",
      success: "Listo. Te has unido. Redirigiendo...",
      loginError: "Error al iniciar sesión",
      acceptError: "No se pudo aceptar la invitación"
    },
    eventDialog: {
      title: "Nuevo evento",
      editTitle: "Editar evento",
      titleLabel: "Título",
      dateLabel: "Fecha",
      morning: "Por la mañana",
      afternoon: "Por la tarde",
      night: "Por la noche",
      allDay: "Todo el día",
      startTime: "Hora inicio",
      endTime: "Hora fin",
      startTimeDialog: "Hora de inicio",
      endTimeDialog: "Hora de fin",
      shareWithGroups: "Compartir con grupos",
      group: "Grupo",
      view: "Visualiza",
      participate: "Participa",
      noGroupsAvailable: "No tienes grupos disponibles.",
      sharedWithGroups: "Compartido con {count} grupo(s).",
      invalidDate: "Selecciona una fecha.",
      invalidDateTime: "Fecha/hora inválida.",
      endTimeAfterStart: "La hora fin debe ser posterior a la hora inicio."
    },
    eventDetails: {
      participatingGroups: "Grupos que participan",
      viewingGroups: "Grupos que visualizan",
      membersCount: "{count} miembro(s)",
      noActiveMembers: "No hay miembros activos en este grupo.",
      you: "tú",
      statusPending: "pendiente",
      statusDeclined: "rechazado",
      statusAccepted: "aceptado",
      statusUnknown: "sin respuesta"
    },
    pwa: {
      title: "Instala Kalendar.io",
      subtitle: "Acceso rápido, pantalla completa y mejor experiencia.",
      iosHelp: "En iPhone/iPad: pulsa Compartir y luego Añadir a pantalla de inicio."
    },
    privacy: {
      back: "Volver al inicio",
      title: "Política de Privacidad",
      intro: "De acuerdo con el Reglamento (UE) 2016/679 (RGPD) y la Ley Orgánica 3/2018 (LOPDGDD), se informa a los usuarios sobre el tratamiento de sus datos personales en la aplicación Kalendario.",
      section1: "1. Responsable del tratamiento",
      controller: "Responsable:",
      email: "Email:",
      country: "País:",
      countryValue: "España",
      section2: "2. Datos personales tratados",
      data1: "Nombre y dirección de correo electrónico",
      data2: "Identificador de usuario",
      data3: "Eventos y calendarios creados",
      data4: "Grupos y relaciones entre usuarios",
      data5: "Preferencias básicas de uso",
      section3: "3. Finalidad del tratamiento",
      purpose1: "Autenticación segura mediante cuenta de Google o correo y contraseña",
      purpose2: "Gestión de calendarios y eventos compartidos",
      purpose3: "Sincronización de información entre miembros de un grupo",
      purpose4: "Envío de notificaciones sobre cambios y nuevos eventos",
      purpose5: "Mantenimiento y mejora del servicio",
      section4: "4. Base legal del tratamiento",
      section4Text: "El tratamiento de los datos se basa en el consentimiento del usuario al iniciar sesión o crear una cuenta y en la necesidad de ejecutar el servicio solicitado.",
      section5: "5. Conservación de los datos",
      section5Text: "Los datos personales se conservarán mientras el usuario mantenga su cuenta activa en Kalendario o hasta que solicite la supresión de los mismos.",
      section6: "6. Destinatarios y terceros",
      section6Text: "Los datos podrán ser tratados mediante servicios de terceros necesarios para el funcionamiento de la aplicación:",
      firebaseProvider: "Google Firebase (Google LLC, Estados Unidos)",
      section7: "7. Derechos del usuario",
      section7Text: "El usuario puede ejercer los derechos de acceso, rectificación, supresión, limitación, oposición y portabilidad enviando un correo electrónico a"
    },
    errors: {
      notAuthenticated: "No autenticado.",
      missingEventId: "Falta eventId.",
      inviteNotFound: "La solicitud ya no existe.",
      invalidInviteState: "Estado de solicitud no válido para aceptar.",
      eventNotFound: "El evento no existe.",
      deleteEventForbidden: "Solo el creador puede eliminar este evento.",
      updateEventForbidden: "Solo el creador puede editar este evento.",
      missingGroupId: "Falta groupId.",
      missingUid: "Falta uid.",
      deleteGroupForbidden: "No tienes permiso para eliminar este grupo.",
      missingPushToken: "Falta token push.",
      missingGoogleIdToken: "No se recibió idToken de Google.",
      invalidInvite: "Invitación no válida",
      expiredInvite: "Invitación caducada"
    }
  },
  en: {
    common: {
      create: "Create",
      cancel: "Cancel",
      close: "Close",
      delete: "Delete",
      leave: "Leave",
      invite: "Invite",
      copy: "Copy",
      copied: "Copied",
      install: "Install",
      notNow: "Not now",
      accept: "Accept",
      decline: "Decline",
      yes: "Yes",
      no: "No",
      ok: "OK",
      clear: "Clear",
      save: "Save",
      event: "Event",
      group: "Group",
      admin: "admin",
      user: "User",
      status: "Status"
    },
    languages: {
      es: "Spanish",
      en: "English",
      pt: "Portuguese"
    },
    nav: {
      calendar: "Calendar",
      groups: "Groups",
      requests: "Requests",
      profile: "Profile"
    },
    login: {
      subtitle: "Sign in or create an account to share calendars with your groups.",
      welcomeTitle: "Welcome to Kalendar.io",
      signInTab: "Sign in",
      registerTab: "Create account",
      nameLabel: "Name",
      emailLabel: "Email address",
      passwordLabel: "Password",
      forgotPassword: "Forgot your password?",
      resetPasswordTitle: "Enter your email address",
      resetPasswordHelp: "We will send you a link to create a new password.",
      resetPasswordButton: "Request password change",
      backToSignIn: "Back to sign in",
      emailButton: "Sign in with email",
      registerButton: "Create account",
      googleButton: "Sign in with Google",
      languageLabel: "Language",
      cookiesPolicy: "Cookies policy",
      legalNotice: "Legal notice",
      privacyPolicy: "Privacy policy",
      acceptLegalPrefix: "I have read and accept the",
      acceptLegalAnd: "and I have read the",
      acceptLegalRequired: "You must accept the legal notice and confirm that you have read the privacy policy to create an account.",
      continueLegalNotice: "By continuing, you accept the",
      continueLegalAnd: "and confirm that you have read the",
      loginError: "Sign-in error",
      authProviderDisabled: "This sign-in method is not enabled in Firebase. Enable the matching provider in Firebase Authentication.",
      emailVerificationSent: "We sent you a verification email. Confirm your email before signing in.",
      emailVerificationRequired: "You must confirm your email before signing in. We sent the verification email again.",
      emailVerifiedNotice: "Email verified. You can now sign in.",
      passwordResetSent: "We sent you an email to reset your password.",
      passwordResetNotice: "Password updated. You can now sign in.",
      requiredField: "Required field",
      invalidEmail: "Invalid email address",
      shortPassword: "Password must be at least 6 characters",
      firebaseAuthErrors: {
        accountExistsWithDifferentCredential: "An account already exists with this email using another sign-in method.",
        credentialAlreadyInUse: "This credential is already linked to another account.",
        emailAlreadyInUse: "An account already exists with this email address.",
        expiredActionCode: "This link has expired. Request a new one.",
        invalidActionCode: "This link is invalid or has already been used.",
        invalidCredential: "Incorrect email or password.",
        invalidEmail: "The email address is invalid.",
        missingPassword: "Enter your password.",
        networkRequestFailed: "Could not connect. Check your connection and try again.",
        operationNotAllowed: "This sign-in method is not enabled in Firebase. Enable the matching provider in Firebase Authentication.",
        popupBlocked: "The browser blocked the sign-in window.",
        popupClosedByUser: "You closed the sign-in window before completing it.",
        requiresRecentLogin: "For security, sign in again and try one more time.",
        tooManyRequests: "Too many attempts. Wait a few minutes and try again.",
        userDisabled: "This account has been disabled.",
        userNotFound: "No account exists with this email address.",
        weakPassword: "The password is too weak. Use at least 6 characters.",
        wrongPassword: "The password is incorrect."
      }
    },
    landing: {
      kicker: "Shared calendars without the noise",
      title: "Organize plans with your groups in one place.",
      subtitle: "Kalendar.io helps you create events, share them with groups, and manage participation requests so everyone knows what is happening and when.",
      cta: "Get started",
      playStore: "Get it on Google Play",
      rightsReserved: "All rights reserved.",
      signIn: "Sign in",
      featuresLabel: "Main features",
      featureCalendarTitle: "A calendar for every plan",
      featureCalendarText: "Create events with dates, times, and context so your schedule stays easy to read.",
      featureGroupsTitle: "Connected groups",
      featureGroupsText: "Share calendars with family, friends, or teams and decide who can view or participate.",
      featureRequestsTitle: "Requests and alerts",
      featureRequestsText: "Receive participation requests and keep your group up to date with notifications.",
      previewMonth: "May 2026",
      previewEventOne: "Afternoon shift",
      previewGroupOne: "Team",
      previewEventTwo: "Family dinner",
      previewGroupTwo: "Family",
      demoCreateTitle: "Create an event",
      demoCreateText: "Add date, time, and details.",
      demoShareTitle: "Share it with groups",
      demoShareText: "Choose who participates or views.",
      demoRequestTitle: "New request",
      demoRequestText: "Your group gets the update instantly."
    },
    calendar: {
      addEvent: "Event",
      editEvent: "Edit",
      viewing: "Viewing",
      participating: "Participating",
      mixedAccess: "Participating and viewing",
      deleteEvent: "Delete",
      noEventsDay: "There are no events for this day.",
      selectDay: "Select a day to see its events",
      filters: "Filters",
      visibility: "Visibility",
      all: "All",
      none: "None",
      allDay: "All day",
      groupFallback: "Group {id}",
      noEventId: "This event has no id (cannot be deleted).",
      confirmDeleteEvent: "Delete this event?",
      deleteEventError: "The event could not be deleted.",
      updateEventError: "The event could not be saved."
    },
    groups: {
      createGroup: "Create group",
      name: "Name",
      noMembers: "There are no members yet.",
      shareLink: "Share this link.",
      copyError: "Could not copy. Please copy the link manually.",
      inviteTitle: "Invite",
      deleteGroupConfirm: "Delete this group?\n\nIts events and members will also be deleted.",
      deleteGroupError: "The group could not be deleted.",
      leaveGroupConfirm: "Are you sure you want to leave this group?",
      leaveGroupError: "You could not leave the group.",
      webShareTitle: "Kalendar.io invitation",
      webShareText: "Join my group on Kalendar.io"
    },
    requests: {
      title: "Requests",
      loginWarning: "Sign in to see your requests.",
      pendingTab: "Pending ({count})",
      archivedTab: "Archived ({count})",
      noPending: "You have no pending requests.",
      noArchived: "You have no archived requests.",
      participation: "Participation",
      declined: "Declined",
      requestLoadError: "Requests could not be loaded.",
      acceptError: "The request could not be accepted.",
      declineError: "The request could not be declined.",
      declinedToast: "Request declined (you can accept it later).",
      noDate: "No date",
      requestedBy: "Requested by {name}"
    },
    profile: {
      title: "Profile",
      settingsTitle: "Settings",
      settingsDescription: "Manage your account, language, and notification preferences.",
      logout: "Log out",
      deleteAccount: "Delete account",
      languageSettings: "App language",
      languageDescription: "Choose how you want the whole interface displayed.",
      notifications: "Notifications",
      notificationSettings: "Notification settings",
      notificationDescription: "Receive a notification when someone requests your participation in an event.",
      notificationLabel: "Event participation requests",
      accountSettings: "Account",
      session: "Session",
      sessionDescription: "Sign out on this device.",
      dangerZone: "Delete account",
      dangerZoneDescription: "Permanently delete your account and all your data.",
      deleteAccountConfirmTitle: "Delete account?",
      deleteAccountConfirmBody: "This action is permanent and cannot be undone. Your account and all your data will be deleted.",
      recentLoginNeeded: "For security, sign in again and repeat account deletion.",
      deleteAccountError: "Error deleting account.",
      saveNotificationError: "Notification setting could not be saved."
    },
    inviteAccept: {
      title: "Invitation",
      loginHint: "Sign in with Google to join the group.",
      joinHint: "Tap to join the group.",
      joinButton: "Join",
      success: "Done. You joined successfully. Redirecting...",
      loginError: "Sign-in error",
      acceptError: "The invitation could not be accepted"
    },
    eventDialog: {
      title: "New event",
      editTitle: "Edit event",
      titleLabel: "Title",
      dateLabel: "Date",
      morning: "Morning",
      afternoon: "Afternoon",
      night: "Night",
      allDay: "All day",
      startTime: "Start time",
      endTime: "End time",
      startTimeDialog: "Start time",
      endTimeDialog: "End time",
      shareWithGroups: "Share with groups",
      group: "Group",
      view: "View",
      participate: "Participate",
      noGroupsAvailable: "You have no available groups.",
      sharedWithGroups: "Shared with {count} group(s).",
      invalidDate: "Select a date.",
      invalidDateTime: "Invalid date/time.",
      endTimeAfterStart: "End time must be later than start time."
    },
    eventDetails: {
      participatingGroups: "Participating groups",
      viewingGroups: "Viewing groups",
      membersCount: "{count} member(s)",
      noActiveMembers: "There are no active members in this group.",
      you: "you",
      statusPending: "pending",
      statusDeclined: "declined",
      statusAccepted: "accepted",
      statusUnknown: "no response"
    },
    pwa: {
      title: "Install Kalendar.io",
      subtitle: "Quick access, full screen, and a better experience.",
      iosHelp: "On iPhone/iPad: tap Share, then Add to Home Screen."
    },
    privacy: {
      back: "Back to home",
      title: "Privacy Policy",
      intro: "In accordance with Regulation (EU) 2016/679 (GDPR), users are informed about the processing of their personal data in the Kalendario application.",
      section1: "1. Data controller",
      controller: "Controller:",
      email: "Email:",
      country: "Country:",
      countryValue: "Spain",
      section2: "2. Personal data processed",
      data1: "Name and email address",
      data2: "User identifier",
      data3: "Created events and calendars",
      data4: "Groups and relationships between users",
      data5: "Basic usage preferences",
      section3: "3. Purpose of processing",
      purpose1: "Secure authentication using a Google account or email and password",
      purpose2: "Management of shared calendars and events",
      purpose3: "Information synchronization among group members",
      purpose4: "Sending notifications about changes and new events",
      purpose5: "Service maintenance and improvement",
      section4: "4. Legal basis",
      section4Text: "Data processing is based on user consent when signing in or creating an account and on the need to provide the requested service.",
      section5: "5. Data retention",
      section5Text: "Personal data will be stored while the user keeps an active account in Kalendario or until deletion is requested.",
      section6: "6. Recipients and third parties",
      section6Text: "Data may be processed by third-party services required for application operation:",
      firebaseProvider: "Google Firebase (Google LLC, United States)",
      section7: "7. User rights",
      section7Text: "Users can exercise their rights of access, rectification, deletion, restriction, objection and portability by sending an email to"
    },
    errors: {
      notAuthenticated: "Not authenticated.",
      missingEventId: "Missing eventId.",
      inviteNotFound: "The request no longer exists.",
      invalidInviteState: "Invalid request status for acceptance.",
      eventNotFound: "The event does not exist.",
      deleteEventForbidden: "Only the creator can delete this event.",
      updateEventForbidden: "Only the creator can edit this event.",
      missingGroupId: "Missing groupId.",
      missingUid: "Missing uid.",
      deleteGroupForbidden: "You do not have permission to delete this group.",
      missingPushToken: "Missing push token.",
      missingGoogleIdToken: "Google idToken was not received.",
      invalidInvite: "Invalid invitation",
      expiredInvite: "Invitation expired"
    }
  },
  pt: {
    common: {
      create: "Criar",
      cancel: "Cancelar",
      close: "Fechar",
      delete: "Excluir",
      leave: "Sair",
      invite: "Convidar",
      copy: "Copiar",
      copied: "Copiado",
      install: "Instalar",
      notNow: "Agora não",
      accept: "Aceitar",
      decline: "Recusar",
      yes: "Sim",
      no: "Não",
      ok: "OK",
      clear: "Limpar",
      save: "Salvar",
      event: "Evento",
      group: "Grupo",
      admin: "admin",
      user: "Usuário",
      status: "Estado"
    },
    languages: {
      es: "Espanhol",
      en: "Inglês",
      pt: "Português"
    },
    nav: {
      calendar: "Calendário",
      groups: "Grupos",
      requests: "Solicitações",
      profile: "Perfil"
    },
    login: {
      subtitle: "Entre ou crie uma conta para compartilhar calendários com seus grupos.",
      welcomeTitle: "Bem-vindo ao Kalendar.io",
      signInTab: "Entrar",
      registerTab: "Criar conta",
      nameLabel: "Nome",
      emailLabel: "Endereço de email",
      passwordLabel: "Senha",
      forgotPassword: "Esqueceu sua senha?",
      resetPasswordTitle: "Digite seu endereço de email",
      resetPasswordHelp: "Enviaremos um link para criar uma nova senha.",
      resetPasswordButton: "Solicitar alteração de senha",
      backToSignIn: "Voltar ao início de sessão",
      emailButton: "Entrar com email",
      registerButton: "Criar conta",
      googleButton: "Entrar com Google",
      languageLabel: "Idioma",
      cookiesPolicy: "Política de cookies",
      legalNotice: "Aviso legal",
      privacyPolicy: "Política de privacidade",
      acceptLegalPrefix: "Li e aceito o",
      acceptLegalAnd: "e li a",
      acceptLegalRequired: "Você deve aceitar o aviso legal e confirmar que leu a política de privacidade para criar uma conta.",
      continueLegalNotice: "Ao continuar, você aceita o",
      continueLegalAnd: "e confirma que leu a",
      loginError: "Erro ao iniciar sessão",
      authProviderDisabled: "Este método de início de sessão não está habilitado no Firebase. Ative o provedor correspondente no Firebase Authentication.",
      emailVerificationSent: "Enviamos um email de verificação. Confirme seu email antes de iniciar sessão.",
      emailVerificationRequired: "Você precisa confirmar seu email antes de entrar. Reenviamos o email de verificação.",
      emailVerifiedNotice: "Email verificado. Você já pode iniciar sessão.",
      passwordResetSent: "Enviamos um email para redefinir sua senha.",
      passwordResetNotice: "Senha atualizada. Você já pode iniciar sessão.",
      requiredField: "Campo obrigatório",
      invalidEmail: "Endereço de email inválido",
      shortPassword: "A senha deve ter pelo menos 6 caracteres",
      firebaseAuthErrors: {
        accountExistsWithDifferentCredential: "Já existe uma conta com este email usando outro método de início de sessão.",
        credentialAlreadyInUse: "Esta credencial já está associada a outra conta.",
        emailAlreadyInUse: "Já existe uma conta com este endereço de email.",
        expiredActionCode: "O link expirou. Solicite um novo.",
        invalidActionCode: "O link não é válido ou já foi usado.",
        invalidCredential: "Email ou senha incorretos.",
        invalidEmail: "O endereço de email não é válido.",
        missingPassword: "Digite sua senha.",
        networkRequestFailed: "Não foi possível conectar. Verifique sua conexão e tente novamente.",
        operationNotAllowed: "Este método de início de sessão não está habilitado no Firebase. Ative o provedor correspondente no Firebase Authentication.",
        popupBlocked: "O navegador bloqueou a janela de início de sessão.",
        popupClosedByUser: "Você fechou a janela de início de sessão antes de concluir.",
        requiresRecentLogin: "Por segurança, entre novamente e tente outra vez.",
        tooManyRequests: "Muitas tentativas. Aguarde alguns minutos e tente novamente.",
        userDisabled: "Esta conta foi desativada.",
        userNotFound: "Não existe nenhuma conta com este endereço de email.",
        weakPassword: "A senha é muito fraca. Use pelo menos 6 caracteres.",
        wrongPassword: "A senha está incorreta."
      }
    },
    landing: {
      kicker: "Calendários compartilhados sem ruído",
      title: "Organize planos com seus grupos em um só lugar.",
      subtitle: "Kalendar.io ajuda você a criar eventos, compartilhá-los com grupos e gerenciar solicitações de participação para que todos saibam o que acontece e quando.",
      cta: "Começar",
      playStore: "Disponível no Google Play",
      rightsReserved: "Todos os direitos reservados.",
      signIn: "Entrar",
      featuresLabel: "Principais funções",
      featureCalendarTitle: "Um calendário para cada plano",
      featureCalendarText: "Crie eventos com data, horário e contexto para consultar sua agenda com clareza.",
      featureGroupsTitle: "Grupos conectados",
      featureGroupsText: "Compartilhe calendários com família, amigos ou equipes e decida quem visualiza ou participa.",
      featureRequestsTitle: "Solicitações e avisos",
      featureRequestsText: "Receba solicitações de participação e mantenha seu grupo atualizado com notificações.",
      previewMonth: "Maio 2026",
      previewEventOne: "Turno da tarde",
      previewGroupOne: "Equipe",
      previewEventTwo: "Jantar em família",
      previewGroupTwo: "Família",
      demoCreateTitle: "Crie um evento",
      demoCreateText: "Adicione data, hora e detalhes.",
      demoShareTitle: "Compartilhe com grupos",
      demoShareText: "Escolha quem participa ou visualiza.",
      demoRequestTitle: "Nova solicitação",
      demoRequestText: "Seu grupo recebe o aviso na hora."
    },
    calendar: {
      addEvent: "Evento",
      editEvent: "Editar",
      viewing: "Visualizo",
      participating: "Participo",
      mixedAccess: "Participa e visualiza",
      deleteEvent: "Excluir",
      noEventsDay: "Não há eventos para este dia.",
      selectDay: "Selecione um dia para ver seus eventos",
      filters: "Filtros",
      visibility: "Visualização",
      all: "Todos",
      none: "Nenhum",
      allDay: "Dia inteiro",
      groupFallback: "Grupo {id}",
      noEventId: "Este evento não tem id (não pode ser excluído).",
      confirmDeleteEvent: "Excluir este evento?",
      deleteEventError: "Não foi possível excluir o evento.",
      updateEventError: "Não foi possível salvar o evento."
    },
    groups: {
      createGroup: "Criar grupo",
      name: "Nome",
      noMembers: "Ainda não há membros.",
      shareLink: "Compartilhe este link.",
      copyError: "Não foi possível copiar. Copie o link manualmente.",
      inviteTitle: "Convidar",
      deleteGroupConfirm: "Excluir este grupo?\n\nSeus eventos e membros também serão excluídos.",
      deleteGroupError: "Não foi possível excluir o grupo.",
      leaveGroupConfirm: "Tem certeza de que quer sair deste grupo?",
      leaveGroupError: "Não foi possível sair do grupo.",
      webShareTitle: "Convite para Kalendar.io",
      webShareText: "Entre no meu grupo no Kalendar.io"
    },
    requests: {
      title: "Solicitações",
      loginWarning: "Entre para ver suas solicitações.",
      pendingTab: "Pendentes ({count})",
      archivedTab: "Arquivadas ({count})",
      noPending: "Você não tem solicitações pendentes.",
      noArchived: "Você não tem solicitações arquivadas.",
      participation: "Participação",
      declined: "Recusada",
      requestLoadError: "Não foi possível carregar as solicitações.",
      acceptError: "Não foi possível aceitar a solicitação.",
      declineError: "Não foi possível recusar a solicitação.",
      declinedToast: "Solicitação recusada (você pode aceitá-la depois).",
      noDate: "Sem data",
      requestedBy: "Solicitado por {name}"
    },
    profile: {
      title: "Perfil",
      settingsTitle: "Configurações",
      settingsDescription: "Gerencie suas preferências de conta, idioma e notificações.",
      logout: "Sair",
      deleteAccount: "Excluir conta",
      languageSettings: "Idioma do aplicativo",
      languageDescription: "Escolha como você quer ver toda a interface.",
      notifications: "Notificações",
      notificationSettings: "Configurações de notificações",
      notificationDescription: "Receba uma notificação quando alguém solicitar sua participação em um evento.",
      notificationLabel: "Solicitações de participação em eventos",
      accountSettings: "Conta",
      session: "Sessão",
      sessionDescription: "Encerre sua sessão neste dispositivo.",
      dangerZone: "Excluir conta",
      dangerZoneDescription: "Exclua permanentemente sua conta e todos os seus dados.",
      deleteAccountConfirmTitle: "Excluir conta?",
      deleteAccountConfirmBody: "Esta ação é permanente e não pode ser desfeita. Sua conta e todos os dados serão excluídos.",
      recentLoginNeeded: "Por segurança, entre novamente e repita a exclusão da conta.",
      deleteAccountError: "Erro ao excluir a conta.",
      saveNotificationError: "Não foi possível salvar a configuração de notificações."
    },
    inviteAccept: {
      title: "Convite",
      loginHint: "Entre com o Google para entrar no grupo.",
      joinHint: "Toque para entrar no grupo.",
      joinButton: "Entrar",
      success: "Pronto. Você entrou. Redirecionando...",
      loginError: "Erro ao iniciar sessão",
      acceptError: "Não foi possível aceitar o convite"
    },
    eventDialog: {
      title: "Novo evento",
      editTitle: "Editar evento",
      titleLabel: "Título",
      dateLabel: "Data",
      morning: "De manhã",
      afternoon: "À tarde",
      night: "À noite",
      allDay: "Dia inteiro",
      startTime: "Hora de início",
      endTime: "Hora de fim",
      startTimeDialog: "Hora de início",
      endTimeDialog: "Hora de fim",
      shareWithGroups: "Compartilhar com grupos",
      group: "Grupo",
      view: "Visualiza",
      participate: "Participa",
      noGroupsAvailable: "Você não tem grupos disponíveis.",
      sharedWithGroups: "Compartilhado com {count} grupo(s).",
      invalidDate: "Selecione uma data.",
      invalidDateTime: "Data/hora inválida.",
      endTimeAfterStart: "A hora de fim deve ser posterior à hora de início."
    },
    eventDetails: {
      participatingGroups: "Grupos que participam",
      viewingGroups: "Grupos que visualizam",
      membersCount: "{count} membro(s)",
      noActiveMembers: "Não há membros ativos neste grupo.",
      you: "você",
      statusPending: "pendente",
      statusDeclined: "recusado",
      statusAccepted: "aceito",
      statusUnknown: "sem resposta"
    },
    pwa: {
      title: "Instale o Kalendar.io",
      subtitle: "Acesso rápido, tela cheia e melhor experiência.",
      iosHelp: "No iPhone/iPad: toque em Compartilhar e depois em Adicionar à Tela de Início."
    },
    privacy: {
      back: "Voltar ao início",
      title: "Política de Privacidade",
      intro: "De acordo com o Regulamento (UE) 2016/679 (RGPD), os usuários são informados sobre o tratamento de seus dados pessoais no aplicativo Kalendario.",
      section1: "1. Responsável pelo tratamento",
      controller: "Responsável:",
      email: "Email:",
      country: "País:",
      countryValue: "Espanha",
      section2: "2. Dados pessoais tratados",
      data1: "Nome e endereço de email",
      data2: "Identificador de usuário",
      data3: "Eventos e calendários criados",
      data4: "Grupos e relações entre usuários",
      data5: "Preferências básicas de uso",
      section3: "3. Finalidade do tratamento",
      purpose1: "Autenticação segura com conta Google ou email e senha",
      purpose2: "Gestão de calendários e eventos compartilhados",
      purpose3: "Sincronização de informações entre membros de um grupo",
      purpose4: "Envio de notificações sobre mudanças e novos eventos",
      purpose5: "Manutenção e melhoria do serviço",
      section4: "4. Base legal do tratamento",
      section4Text: "O tratamento dos dados se baseia no consentimento do usuário ao iniciar sessão ou criar uma conta e na necessidade de prestar o serviço solicitado.",
      section5: "5. Conservação dos dados",
      section5Text: "Os dados pessoais serão conservados enquanto o usuário mantiver sua conta ativa no Kalendario ou até solicitar a exclusão.",
      section6: "6. Destinatários e terceiros",
      section6Text: "Os dados podem ser tratados por serviços de terceiros necessários para o funcionamento do aplicativo:",
      firebaseProvider: "Google Firebase (Google LLC, Estados Unidos)",
      section7: "7. Direitos do usuário",
      section7Text: "O usuário pode exercer os direitos de acesso, retificação, exclusão, limitação, oposição e portabilidade enviando um email para"
    },
    errors: {
      notAuthenticated: "Não autenticado.",
      missingEventId: "Falta eventId.",
      inviteNotFound: "A solicitação não existe mais.",
      invalidInviteState: "Estado da solicitação inválido para aceitar.",
      eventNotFound: "O evento não existe.",
      deleteEventForbidden: "Somente o criador pode excluir este evento.",
      updateEventForbidden: "Somente o criador pode editar este evento.",
      missingGroupId: "Falta groupId.",
      missingUid: "Falta uid.",
      deleteGroupForbidden: "Você não tem permissão para excluir este grupo.",
      missingPushToken: "Falta token push.",
      missingGoogleIdToken: "Não foi recebido idToken do Google.",
      invalidInvite: "Convite inválido",
      expiredInvite: "Convite expirado"
    }
  }
};

function normalizeLocale(locale) {
  if (!locale) return "es";
  const base = locale.toLowerCase().split("-")[0];
  return SUPPORTED_LOCALES.includes(base) ? base : "es";
}

function setDocumentLang(locale) {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("lang", locale);
  }
}

function getInitialLocale() {
  const stored = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  if (stored) return normalizeLocale(stored);

  if (typeof navigator !== "undefined") {
    return normalizeLocale(navigator.language || navigator.userLanguage || "es");
  }

  return "es";
}

const i18n = new VueI18n({
  locale: getInitialLocale(),
  fallbackLocale: "es",
  messages
});

setDocumentLang(i18n.locale);

export function setAppLocale(locale) {
  const next = normalizeLocale(locale);
  i18n.locale = next;
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_KEY, next);
  }
  setDocumentLang(next);
}

export function getSupportedLocales() {
  return [...SUPPORTED_LOCALES];
}

export default i18n;
